import React, { useState, useMemo, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants';
import Icon from '../ui/Icon';
import './Sidebar.css';

const menuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    path: ROUTES.DASHBOARD,
    requiredPermission: null
  },
  {
    id: 'purchase-orders',
    label: 'Purchase Orders',
    icon: 'receipt_long',
    path: ROUTES.PURCHASE_ORDERS,
    requiredPermission: null,
    subItems: [
      {
        id: 'po-list',
        label: 'All POs',
        path: ROUTES.PURCHASE_ORDERS,
        requiredPermission: 'purchaseOrderManagementAccess'
      },
      {
        id: 'po-create',
        label: 'New PO',
        path: ROUTES.PO_CREATE,
        requiredPermission: 'purchaseOrderCreateAccess'
      }
    ]
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: 'inventory',
    path: ROUTES.INVENTORY,
    requiredPermission: null,
    subItems: [
      {
        id: 'inventory-list',
        label: 'All Items',
        path: ROUTES.INVENTORY,
        requiredPermission: 'inventoryManagementAccess'
      },
      {
        id: 'inventory-create',
        label: 'Add Item',
        path: ROUTES.INVENTORY_CREATE,
        requiredPermission: 'inventoryManagementAccess'
      }
    ]
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    icon: 'business',
    path: ROUTES.SUPPLIERS,
    requiredPermission: null,
    subItems: [
      {
        id: 'supplier-list',
        label: 'All Suppliers',
        path: ROUTES.SUPPLIERS,
        requiredPermission: 'supplierManagementAccess'
      },
      {
        id: 'supplier-create',
        label: 'Add Supplier',
        path: ROUTES.SUPPLIER_CREATE,
        requiredPermission: 'supplierCreateAccess'
      }
    ]
  },
  {
    id: 'machines',
    label: 'Machines',
    icon: 'precision_manufacturing',
    path: ROUTES.MACHINES,
    requiredPermission: null,
    subItems: [
      {
        id: 'machines-list',
        label: 'All Machines',
        path: ROUTES.MACHINES,
        requiredPermission: 'machineManagementAccess'
      },
      {
        id: 'machines-create',
        label: 'Add Machine',
        path: ROUTES.MACHINE_CREATE,
        requiredPermission: 'machineManagementAccess'
      }
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'analytics',
    path: ROUTES.REPORTS,
    requiredPermission: null,
    subItems: [
      {
        id: 'machine-reports',
        label: 'Machine Expenses',
        path: ROUTES.REPORTS,
        requiredPermission: null
      }
    ]
  },
  // NOTE: Bank Details menu item removed for normal users
  // NOTE: Configuration menu item removed - feature_list table has been deleted
  {
    id: 'users',
    label: 'Users',
    icon: 'people',
    path: ROUTES.USERS,
    requiredPermission: null,
    subItems: [
      {
        id: 'user-list',
        label: 'All Users',
        path: ROUTES.USERS,
        requiredPermission: null
      },
      {
        id: 'user-create',
        label: 'Create User',
        path: ROUTES.USER_CREATE,
        requiredPermission: 'userCreateAccess'
      }
    ]
  },
  {
    id: 'my-tenant',
    label: 'My Tenant',
    icon: 'business',
    path: ROUTES.MY_TENANT,
    requiredPermission: null
  },
  {
    id: 'configuration',
    label: 'Configuration',
    icon: 'settings',
    path: ROUTES.CONFIGURATION,
    requiredPermission: null,
    systemAdminOnly: true
  }
];

// Mapping between menu item IDs and feature names
const menuItemFeatureMap = {
  'dashboard': 'Dashboard',
  'purchase-orders': 'Purchase Order',
  'inventory': 'Inventory',
  'suppliers': 'Supplier',
  'machines': 'Machine',
  'users': 'Users',
  'my-tenant': 'My Tenant',
  'reports': 'Reports'
};

const Sidebar = ({ isOpen, onClose }) => {
  const { user, accessibleFeatures } = useAuth();
  const location = useLocation();
  const [openDropdowns, setOpenDropdowns] = useState({});

  React.useEffect(() => {
    if (user) {
      console.log('[Sidebar] User permissions debug:', {
        username: user.username,
        role: user.role,
        hasPermissions: !!user.permissions,
        permissions: user.permissions,
        permissionsCount: user.permissions ? Object.keys(user.permissions).length : 0,
        activePermissions: user.permissions ? Object.keys(user.permissions).filter(k => user.permissions[k] === true || user.permissions[k] === 1) : [],
        userCreateAccess: user.permissions?.userCreateAccess,
        userManagementAccess: user.permissions?.userManagementAccess,
        userRolePermissionManagementAccess: user.permissions?.userRolePermissionManagementAccess
      });
    }
  }, [user]);

  const toggleDropdown = (itemId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const isDropdownOpen = (itemId) => {
    return openDropdowns[itemId] || false;
  };

  const filteredMenuItems = useMemo(() => {
    if (!user) return [];

    // Filter menu items based on accessibleFeatures and system admin only items
    return menuItems.filter(item => {
      // Check if item is system admin only
      if (item.systemAdminOnly && user.role !== 'SYSTEM_ADMIN') {
        return false;
      }

      const featureName = menuItemFeatureMap[item.id];
      if (!featureName) {
        // If no feature mapping exists, show the item (for backward compatibility)
        return true;
      }

      // Only show item if feature is accessible (feature is active AND READ permission is active)
      return accessibleFeatures[featureName] === true;
    }).map(item => {
      // Also filter subItems if they exist
      if (item.subItems) {
        return {
          ...item,
          subItems: item.subItems.filter(subItem => {
            // SubItems use the same feature as parent item
            const featureName = menuItemFeatureMap[item.id];
            if (!featureName) {
              return true;
            }
            return accessibleFeatures[featureName] === true;
          })
        };
      }
      return item;
    });
  }, [user, accessibleFeatures]);

  useEffect(() => {
    const activeDropdowns = {};
    filteredMenuItems.forEach(item => {
      if (item.subItems) {
        const hasActiveChild = item.subItems.some(subItem => {
          return location.pathname === subItem.path || location.pathname.startsWith(subItem.path + '/');
        });
        if (hasActiveChild) {
          activeDropdowns[item.id] = true;
        }
      }
    });

    const currentActiveKeys = Object.keys(activeDropdowns).sort();

    setOpenDropdowns(prev => {
      const prevActiveKeys = Object.keys(prev).filter(key => prev[key] === true).sort();

      if (currentActiveKeys.length === prevActiveKeys.length &&
        currentActiveKeys.every((key, index) => key === prevActiveKeys[index])) {
        return prev;
      }

      const newState = {};
      currentActiveKeys.forEach(key => {
        newState[key] = true;
      });

      return newState;
    });
  }, [location.pathname, filteredMenuItems]);

  const handleItemClick = (isDropdownItem = false) => {
    // Close sidebar on mobile when item is clicked, but not for dropdown sub-items
    if (window.innerWidth <= 768 && onClose && !isDropdownItem) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          {/* Navigation */}
          <nav className="sidebar-nav">
            <ul className="sidebar-menu">
              {filteredMenuItems.map((item) => (
                <li key={item.id} className="sidebar-menu-item">
                  {item.subItems ? (
                    // Dropdown menu item
                    <div className="sidebar-dropdown">
                      <button
                        className={`sidebar-dropdown-trigger ${isDropdownOpen(item.id) ? 'open' : ''}`}
                        onClick={() => toggleDropdown(item.id)}
                        aria-expanded={isDropdownOpen(item.id)}
                        aria-haspopup="true"
                      >
                        <span className="sidebar-link-icon">
                          <Icon name={item.icon} size="medium" />
                        </span>
                        <span className="sidebar-link-label">{item.label}</span>
                        <Icon
                          name="keyboard_arrow_down"
                          size="small"
                          className={`sidebar-dropdown-arrow ${isDropdownOpen(item.id) ? 'rotated' : ''}`}
                        />
                      </button>

                      {/* Dropdown Menu */}
                      {isDropdownOpen(item.id) && (
                        <div className="sidebar-dropdown-menu">
                          <ul className="sidebar-dropdown-list">
                            {item.subItems.map((subItem) => (
                              <li key={subItem.id} className="sidebar-dropdown-item">
                                <NavLink
                                  to={subItem.path}
                                  className={({ isActive }) =>
                                    `sidebar-dropdown-link ${isActive ? 'active' : ''}`
                                  }
                                  onClick={() => handleItemClick(true)}
                                  end
                                >
                                  <span className="sidebar-dropdown-link-label">{subItem.label}</span>
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular menu item
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `sidebar-link ${isActive ? 'active' : ''}`
                      }
                      onClick={() => handleItemClick(false)}
                    >
                      <span className="sidebar-link-icon">
                        <Icon name={item.icon} size="medium" />
                      </span>
                      <span className="sidebar-link-label">{item.label}</span>
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
